/**
 * Resolves API base through `/api/v1` (matches Express `app.use("/api/v1", …)`).
 */

function normalizeApiBase(raw) {
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s) return "";
  return s.replace(/\/$/, "");
}

/**
 * Env mistakes like `VITE_API_BASE_URL=:5009` or `5009` yield `http://:5009` and break requests
 * (browser shows `:5009/api/...`, connection refused). Accept common shorthands.
 */
function coerceToAbsoluteApiBase(raw) {
  const s = normalizeApiBase(raw);
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  if (/^:\d+$/.test(s)) return `http://localhost${s}`;
  if (/^\d+$/.test(s)) return `http://localhost:${s}`;
  if (/^[a-zA-Z0-9.-]+:\d+$/.test(s)) return `http://${s}`;
  return s;
}

function withApiPathSegment(base) {
  if (!base) return "";
  const b = normalizeApiBase(base);
  if (!b) return "";

  if (/^https?:\/\//i.test(b)) {
    try {
      const u = new URL(b);
      const path = u.pathname.replace(/\/$/, "") || "";
      const segments = path.split("/").filter(Boolean);
      if (segments[0] === "api") {
        return `${u.origin}${path === "" ? "/api" : path}`;
      }
      if (segments.length === 0) {
        return `${u.origin}/api`;
      }
      return b;
    } catch {
      return `${b}/api`;
    }
  }

  const trimmed = b.replace(/^\/+/, "").replace(/\/+$/, "");
  const segments = trimmed.split("/").filter(Boolean);
  if (segments[0] === "api") {
    return `/${trimmed}`;
  }
  if (segments.length === 0) {
    return "/api";
  }
  return b.startsWith("/") ? b : `/${b}`;
}

const isViteDev = import.meta.env.DEV;
const fromEnv = coerceToAbsoluteApiBase(import.meta.env.VITE_API_BASE_URL);
/** When `npm run dev` and `VITE_API_BASE_URL` is unset — same hosted dev API as landing. */
const devHostedApiFallback = "https://dev.oraclemark.com";

const rawBase = isViteDev ? fromEnv || devHostedApiFallback : fromEnv;
const apiRoot = withApiPathSegment(rawBase) || "/api";

/** e.g. https://dev.oraclemark.com/api/v1 in dev when env is unset */
export function getApiV1Base() {
  return `${apiRoot}/v1`.replace(/\/+$/, "");
}
